// E8LotteryProbabilityClosedTask.js
// Zadanie zamkniete E8: prawdopodobienstwo w losowaniu losu pustego.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function fractionLatex(value, denominator) {
  return `\\frac{${value}}{${denominator}}`;
}

function createOptions(correctEmpty, total, winningCount) {
  const candidates = [
    correctEmpty,
    correctEmpty - 1,
    winningCount - 1,
    winningCount,
    correctEmpty + 1,
    total - correctEmpty + 1,
  ];

  const unique = [];
  candidates.forEach((value) => {
    if (
      Number.isInteger(value) &&
      value > 0 &&
      value < total &&
      !unique.includes(value)
    ) {
      unique.push(value);
    }
  });

  while (unique.length < 4) {
    const value = correctEmpty + unique.length + 1;
    if (value < total && !unique.includes(value)) unique.push(value);
    else break;
  }

  const values = shuffle(unique.slice(0, 4));
  return {
    options: values.map((value, index) => ({
      label: LETTERS[index],
      numerator: value,
      denominator: total,
      latex: fractionLatex(value, total),
    })),
    correctIndex: values.indexOf(correctEmpty),
  };
}

function createOriginalTask() {
  const options = [26, 27, 35, 36].map((value, index) => ({
    label: LETTERS[index],
    numerator: value,
    denominator: 72,
    latex: fractionLatex(value, 72),
  }));

  return {
    type: "E8LotteryProbabilityClosedTask",
    data: {
      total: 72,
      firstWinningFrom: 1,
      firstWinningTo: 9,
      secondWinningFrom: 46,
      secondWinningTo: 72,
      firstWinningCount: 9,
      secondWinningCount: 27,
      winningCount: 36,
      emptyCount: 36,
      options,
      correctIndex: 3,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 10,
    },
    tags: ["e8", "prawdopodobienstwo", "losowanie"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const total = pick([48, 60, 64, 72, 80, 90]);
  const firstWinningCount = pick([6, 8, 9, 10, 12]);
  const secondWinningCount = pick([14, 18, 20, 24, 27, 30]);
  const winningCount = firstWinningCount + secondWinningCount;
  const emptyCount = total - winningCount;

  if (emptyCount <= 0) return createOriginalTask();

  const secondWinningFrom = total - secondWinningCount + 1;
  const { options, correctIndex } = createOptions(
    emptyCount,
    total,
    winningCount
  );

  return {
    type: "E8LotteryProbabilityClosedTask",
    data: {
      total,
      firstWinningFrom: 1,
      firstWinningTo: firstWinningCount,
      secondWinningFrom,
      secondWinningTo: total,
      firstWinningCount,
      secondWinningCount,
      winningCount,
      emptyCount,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "E8_2025_MAJ_10" },
    tags: ["e8", "prawdopodobienstwo", "losowanie"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Na loterii przygotowano <InlineMath math={`${data.total}`} /> losy
        ponumerowane kolejnymi liczbami naturalnymi od{" "}
        <InlineMath math="1" /> do <InlineMath math={`${data.total}`} />.
        Wygrywają losy oznaczone numerami od{" "}
        <InlineMath math={`${data.firstWinningFrom}`} /> do{" "}
        <InlineMath math={`${data.firstWinningTo}`} /> oraz losy oznaczone
        numerami od <InlineMath math={`${data.secondWinningFrom}`} /> do{" "}
        <InlineMath math={`${data.secondWinningTo}`} />. Pozostałe losy są
        puste.
      </p>
      <p>
        Ada jako pierwsza losuje jeden los. Jakie jest prawdopodobieństwo tego,
        że Ada wylosuje pusty los? Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={option.latex} display />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        value: index,
        label: option.label,
        textNode: option.label,
      }))}
      value={choiceIndex}
      onChange={(index) => {
        const option = data.options[index];
        onChange({
          choiceIndex: index,
          label: option?.label,
          value: option?.numerator,
        });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return <ChoiceOptionMath letter={option.label} math={option.latex} display />;
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <ChoiceOptionMath letter={option.label} math={option.latex} display />
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Liczymy losy wygrywające z pierwszego zakresu.
      </p>
      <BlockMath
        math={`${data.firstWinningTo}-${data.firstWinningFrom}+1=${data.firstWinningCount}`}
      />
      <p>
        Dodajemy <InlineMath math="1" />, bo liczymy także oba końce zakresu.
      </p>
      <p>
        <strong>Krok 2.</strong> Liczymy losy wygrywające z drugiego zakresu.
      </p>
      <BlockMath
        math={`${data.secondWinningTo}-${data.secondWinningFrom}+1=${data.secondWinningCount}`}
      />
      <p>
        <strong>Krok 3.</strong> Obliczamy łączną liczbę losów wygrywających.
      </p>
      <BlockMath
        math={`${data.firstWinningCount}+${data.secondWinningCount}=${data.winningCount}`}
      />
      <p>
        <strong>Krok 4.</strong> Pozostałe losy są puste, więc odejmujemy od
        liczby wszystkich losów.
      </p>
      <BlockMath math={`${data.total}-${data.winningCount}=${data.emptyCount}`} />
      <p>
        <strong>Krok 5.</strong> Prawdopodobieństwo pustego losu to liczba
        pustych losów podzielona przez liczbę wszystkich losów.
      </p>
      <BlockMath
        math={`P=\\frac{${data.emptyCount}}{${data.total}}`}
      />
      <p>
        Poprawna jest odpowiedź{" "}
        <ChoiceOptionMath letter={correctOption.label} math={correctOption.latex} display />
        .
      </p>
    </div>
  );
}
